<template>
  <div class="flex items-center justify-between gap-2 pb-4 pt-2">
    <!-- Subaccount, Search, Filter -->
    <div class="flex min-w-0 flex-1 items-center gap-3">
      <!-- Subaccount selection -->
      <LicencesTableSubAccountSelector
        v-if="
          !isSSO &&
          props.application?.sub_accounts &&
          Object.keys(props.application.sub_accounts).length > 1
        "
        v-model:active-sub-account-id="activeSubAccountId"
        :application="props.application" />
      <!-- Search -->
      <SmInput
        v-model="searchQuery"
        class="w-[200px] max-w-xs"
        size="small"
        outline
        :label="i18n.t('views.users.table.searchPlaceholder')" />
      <!-- Filter -->
      <SmDropdown
        :close-on-click-inside="false"
        dropdown-class="w-[42rem] max-w-7xl">
        <template #trigger>
          <SmButton outline size="small">
            <div class="flex items-center gap-2">
              <Filter :size="18" />
              <span class="button-text">{{ i18n.t('filter') }}</span>
              <span v-if="filterCount"> ({{ filterCount }}) </span>
            </div>
          </SmButton>
        </template>
        <div class="p-4">
          <AccountFilterDialog
            v-model:filter="filter"
            :software="props.application"
            :software-licenses="props.applicationLicenses"
            :show-applications-filter="false"
            @apply-filter="handleApplyFilter" />
        </div>
      </SmDropdown>
    </div>

    <!-- Export, Import, Add -->
    <div class="flex items-center">
      <!-- Export -->
      <div
        v-require-permission="CompanyAccessRole.ADMIN"
        class="flex items-center">
        <SmButton v-if="props.showImportExport" @click="emit('exportLicenses')">
          <div class="flex items-center gap-2">
            <Download :size="18" class="text-white" />
            <span class="button-text">
              {{ i18n.t('export') }}
            </span>
          </div>
        </SmButton>

        <!-- Import -->
        <SmButton
          v-if="
            props.application.type == SoftwareTypes.MANUALLY &&
            props.showImportExport
          "
          @click="emit('importLicenses')">
          <div class="flex items-center gap-2">
            <Upload :size="18" class="text-white" />
            <span class="button-text">
              {{ i18n.t('import') }}
            </span>
          </div>
        </SmButton>

        <!-- Add user -->
        <SmTooltip
          v-if="props.application.information.implements_add_user_method"
          placement="top">
          <template #content>
            <!-- Readonly -->
            <span v-if="props.application.read_only">
              {{ i18n.t('readonlyDesc') }}
            </span>
            <span v-else-if="props.application.information.sso_provider">
              {{ i18n.t('readonlySSODesc') }}
            </span>
            <span v-else>
              {{ i18n.t('addAccount') }}
            </span>
          </template>

          <SmButton
            :disabled="
              props.application.read_only ||
              props.application.information.sso_provider
            "
            type="primary"
            size="small"
            class="ml-2"
            @click="() => emit('openAddUser')">
            <UserPlus class="text-white" :size="18" />
          </SmButton>
        </SmTooltip>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
  import { useI18n } from 'vue-i18n'
  import { useRoute } from 'vue-router'
  import { Download, Filter, Upload, UserPlus } from 'lucide-vue-next'
  import {
    CompanyAccessRole,
    SoftwareLicense,
    SoftwareOut,
    SoftwareTypes,
  } from '@/client'
  import SmTooltip from '@/components/sm/SmTooltip.vue'
  import { AccountFilters } from '@/stores/account/account.filter'

  const emit = defineEmits<{
    openAddUser: []
    importLicenses: []
    exportLicenses: []
  }>()

  const props = defineProps<{
    licenseCount: number
    application: SoftwareOut
    applicationLicenses: SoftwareLicense[]
    showImportExport: boolean
  }>()

  const i18n = useI18n()
  const route = useRoute()
  const isSSO = computed(() => route.query.sso === 'true')

  const searchQuery = defineModel<string>('searchQuery', { default: '' })
  const activeSubAccountId = defineModel<string>('activeSubAccountId')
  const filter = defineModel<AccountFilters>('filter', { default: {} })

  // Handle filter count
  const filterCount = computed(() => {
    return Object.keys(filter.value).filter((key) => {
      if (['subAccountId', 'applicationId'].includes(key)) return false
      const filterKey = filter.value[key as keyof AccountFilters]
      return filterKey !== undefined && filterKey !== null
    }).length
  })

  watch(searchQuery, () => {
    const trimmedQuery = searchQuery.value.trim()
    filter.value.search = trimmedQuery.length > 0 ? trimmedQuery : undefined
  })

  watch(activeSubAccountId, () => {
    filter.value.subAccountId = activeSubAccountId.value || undefined
  })

  const visibilityFilterDialog = ref<boolean>(false)
  function handleApplyFilter() {
    visibilityFilterDialog.value = false
  }
</script>
